import React from "react";
import { Box, Skeleton } from "@mui/material";

const RacePageSkeleton = ({ isMediumScreen }) => {
  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: isMediumScreen ? 0 : "15px",
          pt: isMediumScreen ? 0 : "38px",
          pl: isMediumScreen ? 0 : "38px",
          pb: isMediumScreen ? 18 : 0,
          mb: isMediumScreen ? 0 : 12,
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {/* Race Info Skeleton */}
        {!isMediumScreen && (
          <Box display="flex" justifyContent="space-between" mb={4}>
            <Box>
              <Skeleton
                variant="text"
                width={200}
                height={40}
                sx={{ bgcolor: "grey.800" }}
              />
              <Skeleton
                variant="text"
                width={120}
                height={24}
                sx={{ bgcolor: "grey.800" }}
              />
            </Box>
            <Box>
              <Skeleton
                variant="text"
                width={150}
                height={40}
                sx={{ bgcolor: "grey.800" }}
              />
              <Skeleton
                variant="text"
                width={100}
                height={24}
                sx={{ bgcolor: "grey.800" }}
              />
            </Box>
          </Box>
        )}

        {/* Video Player Skeleton */}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={400}
          sx={{
            bgcolor: "grey.800",
            borderRadius: isMediumScreen ? 0 : "12px",
          }}
        />

        {/* Racer Odds Skeleton */}
        <Box px={isMediumScreen ? 3 : 0} mt={4}>
          <Skeleton
            variant="text"
            width={150}
            height={32}
            sx={{ bgcolor: "grey.800" }}
          />
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 2,
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {[1, 2, 3, 4].map((item) => (
              <Skeleton
                key={item}
                variant="rectangular"
                width={200}
                height={80}
                sx={{
                  bgcolor: "grey.800",
                  flexShrink: 0,
                  borderRadius: "12px",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Bet Table Skeleton */}
        <Box mt={4}>
          <Box sx={{ overflowX: "auto" }}>
            {[1, 2, 3, 4].map((row) => (
              <Box
                key={row}
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 2,
                  p: 2,
                  borderRadius: "8px",
                  bgcolor: "grey.900",
                }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ bgcolor: "grey.800" }}
                />
                <Skeleton
                  variant="text"
                  width={100}
                  height={40}
                  sx={{ bgcolor: "grey.800" }}
                />
                <Skeleton
                  variant="text"
                  width={80}
                  height={40}
                  sx={{ bgcolor: "grey.800" }}
                />
                <Skeleton
                  variant="text"
                  width={100}
                  height={40}
                  sx={{ bgcolor: "grey.800" }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Right Sidebar Skeleton */}
      {!isMediumScreen && (
        <Box
          sx={{
            width: { lg: 370, sm: 300 },
            p: 3,
            height: "auto",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box
            sx={{
              mt: 3,
              p: 3,
              mb: 12,
              borderRadius: "16px",
              bgcolor: "grey.900",
            }}
          >
            {/* User Details Skeleton */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Skeleton
                variant="circular"
                width={57}
                height={57}
                sx={{ bgcolor: "grey.800" }}
              />
              <Box>
                <Skeleton
                  variant="text"
                  width={100}
                  height={24}
                  sx={{ bgcolor: "grey.800" }}
                />
                <Skeleton
                  variant="text"
                  width={80}
                  height={24}
                  sx={{ bgcolor: "grey.800" }}
                />
              </Box>
            </Box>

            {/* Bet Details Skeleton */}
            <Box mt={3}>
              <Skeleton
                variant="text"
                width={120}
                height={34}
                sx={{ bgcolor: "grey.800" }}
              />
              <Box mt={2}>
                {[1, 2, 3].map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      my: 2,
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width={100}
                      height={34}
                      sx={{ bgcolor: "grey.800" }}
                    />
                    <Skeleton
                      variant="text"
                      width={100}
                      height={34}
                      sx={{ bgcolor: "grey.800" }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Racer Profile Skeleton */}
            <Box mt={4}>
              <Skeleton
                variant="text"
                width={100}
                height={34}
                sx={{ bgcolor: "grey.800" }}
              />
              <Box mt={4}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={48}
                  sx={{
                    bgcolor: "grey.800",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            </Box>

            {/* Wager Skeleton */}
            <Box mt={4}>
              <Skeleton
                variant="text"
                width={80}
                height={24}
                sx={{ bgcolor: "grey.800" }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={50}
                sx={{
                  bgcolor: "grey.800",
                  mt: 2,
                  borderRadius: "12px",
                }}
              />
              <Box mt={2} display="flex" gap={1}>
                {[1, 2, 3, 4].map((item) => (
                  <Skeleton
                    key={item}
                    variant="rectangular"
                    width={60}
                    height={32}
                    sx={{
                      bgcolor: "grey.800",
                      borderRadius: "8px",
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Place Bet Button Skeleton */}
            <Box mt={4}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={56}
                sx={{
                  bgcolor: "grey.800",
                  borderRadius: "40px",
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RacePageSkeleton;
